'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { DayPicker, Matcher } from 'react-day-picker'
import { format } from 'date-fns'
import { getDateFnsLocale } from '@/lib/date-fns-locale'
import clsx from "clsx";
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import "react-day-picker/style.css";
import "@/styles/alsero-day-picker.css";

interface DatePickerCustomProps {
    selected: Date | undefined
    onSelect: (date: Date | undefined) => void
    disabled?: Matcher | Matcher[]
    label: string
    /** Label ile diğer form alanları aynı boyutta olsun diye (örn. font-semibold text-sm) */
    labelClassName?: string
    /** Input butonunun görünümü için dışarıdan class override */
    buttonClassName?: string
    isDisabled?: boolean
    /** Takvim açıldığında gösterilecek ay (örn. yat fiyat dönemi başlangıcı); verilmezse selected veya bugün */
    defaultMonth?: Date
    /** Takvimde gezinebilecek en erken ay (fiyat dönemi başlangıcı) */
    startMonth?: Date
    /** Takvimde gezinebilecek en geç ay (fiyat dönemi bitişi) */
    endMonth?: Date
    /** Takvim ay/gün dili; verilmezse LanguageVariablesContext.language kullanılır */
    language?: string
    /** Aynı sayfada birden fazla picker için benzersiz id */
    month?: Date
    onMonthChange?: (month: Date) => void
    inputId?: string
}

/** 7 sütun × ~42px + padding; dar input genişliğine sıkıştırma */
const CALENDAR_POPOVER_WIDTH = 340

export default function DatePickerCustom({ selected, onSelect, disabled, label, labelClassName, buttonClassName, isDisabled, defaultMonth, startMonth, endMonth, language: languageProp, inputId, month, onMonthChange }: DatePickerCustomProps) {
    const buttonId = inputId ?? 'date-picker-input'
    const { getVariable, language: contextLanguage } = useLanguageVariables()
    const dateLocale = getDateFnsLocale(languageProp ?? contextLanguage)
    const [showPicker, setShowPicker] = useState(false)
    const pickerRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const calendarRef = useRef<HTMLDivElement>(null)
    const [calendarPos, setCalendarPos] = useState<{ top: number; left: number; width: number } | null>(null)

    const updateCalendarPosition = useCallback(() => {
        const anchor = buttonRef.current
        if (!anchor) return
        const rect = anchor.getBoundingClientRect()
        const width = CALENDAR_POPOVER_WIDTH
        let left = rect.left + rect.width / 2 - width / 2
        if (left + width > window.innerWidth - 8) {
            left = window.innerWidth - width - 8
        }
        left = Math.max(8, left)
        setCalendarPos({
            top: rect.bottom + 8,
            left,
            width,
        })
    }, [])

    useEffect(() => {
        if (!showPicker) {
            setCalendarPos(null)
            return
        }
        updateCalendarPosition()
        const onScrollOrResize = () => updateCalendarPosition()
        window.addEventListener('scroll', onScrollOrResize, true)
        window.addEventListener('resize', onScrollOrResize)
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true)
            window.removeEventListener('resize', onScrollOrResize)
        }
    }, [showPicker, updateCalendarPosition])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (pickerRef.current?.contains(target)) return
            if (calendarRef.current?.contains(target)) return
            setShowPicker(false)
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div ref={pickerRef} className="w-full relative">
            <label htmlFor={buttonId} className={labelClassName ?? "text-alsero-dark-gray text-base sm:text-lg mb-1"}>{label}</label>
            <button
                ref={buttonRef}
                id={buttonId}
                type="button"
                onClick={() => {
                    if (isDisabled) return
                    setShowPicker((open) => {
                        if (!open) updateCalendarPosition()
                        return !open
                    })
                }}
                disabled={isDisabled}
                aria-expanded={showPicker}
                aria-haspopup="dialog"
                aria-label={`Select date. ${selected ? `Currently selected: ${format(selected, 'dd/MM/yyyy', { locale: dateLocale })}` : 'No date selected'}`}
                className={`relative flex flex-row justify-between items-center px-4 sm:px-6 py-3 bg-white border border-gray-200 rounded-lg w-full min-h-[50px] sm:min-h-[60px] transition-all ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${buttonClassName ?? ''}`}
            >
                <div className="flex flex-row justify-between gap-2 items-center w-full">
                    <div className="flex flex-row gap-3 items-center">
                        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray" aria-hidden="true" focusable="false">
                            <path fillRule="evenodd" clipRule="evenodd" d="M18.0348 17.1251C18.0348 18.5221 16.9073 19.6496 15.5102 19.6496H2.94204C1.54499 19.6496 0.41748 18.5221 0.41748 17.1251V4.55692C0.41748 3.15986 1.54499 2.03236 2.94204 2.03236H5.70267V1.1603C5.70409 0.953032 5.77713 0.752628 5.90941 0.593053C6.0417 0.433478 6.22508 0.324548 6.4285 0.284724L6.58353 0.27063C7.06977 0.27063 7.46439 0.64764 7.46439 1.1603V2.03236H10.9879V1.1603C10.9893 0.953032 11.0623 0.752628 11.1946 0.593053C11.3269 0.433478 11.5103 0.324548 11.7137 0.284724L11.8687 0.27063C12.355 0.27063 12.7496 0.64764 12.7496 1.1603V2.03236H15.5102C16.9073 2.03236 18.0348 3.15986 18.0348 4.55692V17.1251ZM2.17921 7.31754V16.7076C2.17921 17.3594 2.70773 17.8879 3.35957 17.8879H15.0927C15.7445 17.8879 16.273 17.3594 16.273 16.7076V7.31754H2.17921ZM4.8218 14.3645C5.25343 14.3645 5.61458 14.6869 5.68857 15.0903L5.70267 15.2453C5.70267 15.6769 5.38027 16.0381 4.97683 16.1121L4.8218 16.1262C4.58889 16.1239 4.36617 16.0303 4.20147 15.8657C4.03677 15.701 3.94323 15.4782 3.94094 15.2453C3.94094 14.8137 4.26333 14.4525 4.66677 14.3786L4.8218 14.3645ZM9.22612 14.3645C9.65775 14.3645 10.0189 14.6869 10.0929 15.0903L10.107 15.2453C10.107 15.6769 9.78459 16.0381 9.38116 16.1121L9.22612 16.1262C8.99321 16.1239 8.77049 16.0303 8.60579 15.8657C8.4411 15.701 8.34755 15.4782 8.34526 15.2453C8.34526 14.8137 8.66765 14.4525 9.07109 14.3786L9.22612 14.3645ZM13.6304 14.3645C14.0621 14.3645 14.4232 14.6869 14.4972 15.0903L14.5113 15.2453C14.5113 15.6769 14.1889 16.0381 13.7855 16.1121L13.6304 16.1262C13.3975 16.1239 13.1748 16.0303 13.0101 15.8657C12.8454 15.701 12.7519 15.4782 12.7496 15.2453C12.7496 14.8137 13.072 14.4525 13.4754 14.3786L13.6304 14.3645ZM4.8218 11.7219C5.25343 11.7219 5.61458 12.0443 5.68857 12.4477L5.70267 12.6027C5.70267 13.0344 5.38027 13.3955 4.97683 13.4695L4.8218 13.4836C4.58889 13.4813 4.36617 13.3878 4.20147 13.2231C4.03677 13.0584 3.94323 12.8356 3.94094 12.6027C3.94094 12.1711 4.26333 11.81 4.66677 11.736L4.8218 11.7219ZM9.22612 11.7219C9.65775 11.7219 10.0189 12.0443 10.0929 12.4477L10.107 12.6027C10.107 13.0344 9.78459 13.3955 9.38116 13.4695L9.22612 13.4836C8.99321 13.4813 8.77049 13.3878 8.60579 13.2231C8.4411 13.0584 8.34755 12.8356 8.34526 12.6027C8.34526 12.1711 8.66765 11.81 9.07109 11.736L9.22612 11.7219ZM13.6304 11.7219C14.0621 11.7219 14.4232 12.0443 14.4972 12.4477L14.5113 12.6027C14.5113 13.0344 14.1889 13.3955 13.7855 13.4695L13.6304 13.4836C13.3975 13.4813 13.1748 13.3878 13.0101 13.2231C12.8454 13.0584 12.7519 12.8356 12.7496 12.6027C12.7496 12.1711 13.072 11.81 13.4754 11.736L13.6304 11.7219ZM4.8218 9.07927C5.25343 9.07927 5.61458 9.40167 5.68857 9.8051L5.70267 9.96014C5.70267 10.3918 5.38027 10.7529 4.97683 10.8269L4.8218 10.841C4.58889 10.8387 4.36617 10.7452 4.20147 10.5805C4.03677 10.4158 3.94323 10.193 3.94094 9.96014C3.94094 9.52851 4.26333 9.16736 4.66677 9.09337L4.8218 9.07927ZM9.22612 9.07927C9.65775 9.07927 10.0189 9.40167 10.0929 9.8051L10.107 9.96014C10.107 10.3918 9.78459 10.7529 9.38116 10.8269L9.22612 10.841C8.99321 10.8387 8.77049 10.7452 8.60579 10.5805C8.4411 10.4158 8.34755 10.193 8.34526 9.96014C8.34526 9.52851 8.66765 9.16736 9.07109 9.09337L9.22612 9.07927ZM13.6304 9.07927C14.0621 9.07927 14.4232 9.40167 14.4972 9.8051L14.5113 9.96014C14.5113 10.3918 14.1889 10.7529 13.7855 10.8269L13.6304 10.841C13.3975 10.8387 13.1748 10.7452 13.0101 10.5805C12.8454 10.4158 12.7519 10.193 12.7496 9.96014C12.7496 9.52851 13.072 11.81 13.4754 9.09337L13.6304 9.07927ZM2.17921 5.55582H16.273V4.97444C16.273 4.32261 15.7445 3.79409 15.0927 3.79409H3.35957C2.70773 3.79409 2.17921 4.32261 2.17921 4.97444V5.55582Z"/>
                        </svg>
                        <span className={`text-base sm:text-lg ${selected ? 'text-alsero-dark-gray' : 'text-alsero-light-gray'}`}>
                            {selected ? format(selected, 'dd/MM/yyyy', { locale: dateLocale }) : getVariable('date_picker_select_date', 'Select date')}
                        </span>
                    </div>
                    <svg width="16" height="8" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg"
                         className={clsx('transition-transform duration-200 fill-alsero-dark-gray', showPicker && 'rotate-180')}
                         aria-hidden="true"
                         focusable="false"
                    >
                        <path fillRule="evenodd" clipRule="evenodd" d="M14.7674 0.454837C15.1264 0.7968 15.1264 1.35123 14.7674 1.6932L8.63965 7.53088C8.2807 7.87284 7.69872 7.87284 7.33976 7.53088L1.21201 1.6932C0.853056 1.35123 0.853056 0.7968 1.21201 0.454836C1.57097 0.112873 2.15295 0.112873 2.5119 0.454836C5.57922 3.37696 10.4002 3.37696 13.4675 0.454837C13.8265 0.112873 14.4084 0.112873 14.7674 0.454837Z"/>
                    </svg>
                </div>
            </button>
            {showPicker && !isDisabled && typeof document !== 'undefined' && calendarPos &&
                createPortal(
                    <motion.div
                        ref={calendarRef}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        variants={{
                            open: {
                                opacity: 1,
                                scale: 1,
                                y: 0,
                                transition: {
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 24,
                                    staggerChildren: 0.05,
                                    delayChildren: 0.1
                                }
                            },
                            closed: {
                                opacity: 0,
                                scale: 0.95,
                                y: -10,
                                transition: {
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 24
                                }
                            }
                        }}
                        className="alsero-date-picker-popover bg-white rounded-xl shadow-xl border border-gray-100 p-3 sm:p-4 box-border"
                        style={{
                            position: 'fixed',
                            top: calendarPos.top,
                            left: calendarPos.left,
                            width: calendarPos.width,
                            maxWidth: 'calc(100vw - 16px)',
                            zIndex: 10050,
                        }}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Date picker calendar"
                    >
                        <DayPicker
                            mode="single"
                            selected={selected}
                            onSelect={(date) => {
                                onSelect(date)
                                setShowPicker(false)
                            }}
                            disabled={disabled}
                            month={month}
                            onMonthChange={onMonthChange}
                            defaultMonth={defaultMonth ?? selected}
                            startMonth={startMonth}
                            endMonth={endMonth}
                            locale={dateLocale}
                        />
                    </motion.div>,
                    document.body
                )}
        </div>
    )
}

